import React, { useEffect, useMemo, useState } from "react";
import { Layout } from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Skeleton } from "@/components/ui/skeleton";
import { useT } from "@/i18n/I18nContext";
import { getStockMovements, downloadStockMovementsXlsx, fmt } from "@/lib/api";
import {
  ArrowLeftRight, ArrowDownToLine, ArrowUpFromLine, Scale, ListChecks,
  Download, PackageSearch, Trash2, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";

const ALL = "__all__";

// First day of the current month -> today, as YYYY-MM-DD (the sensible default window).
const monthStart = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
};
const today = () => new Date().toISOString().slice(0, 10);

const DIRECTION_STYLE = {
  in: "badge-ok",
  out: "badge-bad",
};

// Per-reason accent colors for the losses panel (design tokens only).
// Custom tenant reasons (plain labels) share the brand accent.
const REASON_COLOR = {
  expired: "hsl(var(--warning))",
  damaged: "hsl(var(--danger))",
  quality: "hsl(var(--info))",
  other: "hsl(var(--muted-foreground))",
  unspecified: "hsl(var(--muted-foreground))",
};
const CUSTOM_REASON_COLOR = "hsl(var(--brand))";
const BUILTIN_REASONS = ["expired", "damaged", "quality", "other", "unspecified"];

export default function StockMovements() {
  const { t, td } = useT();
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(today());
  const [product, setProduct] = useState(ALL);
  const [data, setData] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  const params = useMemo(
    () => ({ start, end, ...(product === ALL ? {} : { product }) }),
    [start, end, product],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getStockMovements(params)
      .then((d) => {
        if (!alive) return;
        setData(d);
        if (Array.isArray(d.products)) {
          // Keep the full product list stable across filtering so the dropdown never empties out.
          setProducts((prev) => (product === ALL ? d.products : (prev.length ? prev : d.products)));
        }
      })
      .catch(() => { if (alive) { setData(null); toast.error(t("stockMovements.loadError")); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const typeLabel = (ty) => t(`stockMovements.type.${ty}`);
  const reasonLabel = (r) => (BUILTIN_REASONS.includes(r) ? t(`stockMovements.losses.reason.${r}`) : r);

  const onExport = async () => {
    setExporting(true);
    try {
      await downloadStockMovementsXlsx(params);
      toast.success(t("stockMovements.exportDone"));
    } catch {
      toast.error(t("stockMovements.exportError"));
    } finally {
      setExporting(false);
    }
  };

  const totals = data?.totals || { in_kg: 0, out_kg: 0, net_kg: 0, movement_count: 0, product_count: 0 };
  const losses = data?.losses || { total_kg: 0, total_count: 0, total_value: 0, currency: "RON", uncovered_kg: 0, by_reason: [] };
  const rows = data?.rows || [];
  const movements = data?.movements || [];
  const hasRows = rows.length > 0;
  const hasMoves = movements.length > 0;

  return (
    <Layout
      actions={
        <Button
          size="sm"
          onClick={onExport}
          disabled={exporting || loading}
          data-testid="stock-movements-export"
          className="gap-1.5"
        >
          <Download className="h-4 w-4" /> {t("stockMovements.export")}
        </Button>
      }
    >
      <div className="mb-5 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold tracking-tight">
            <ArrowLeftRight className="h-6 w-6 text-[hsl(var(--brand))]" /> {t("stockMovements.title")}
          </h1>
          <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t("stockMovements.subtitle")}</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4">
        <CardContent className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-end">
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("stockMovements.from")}</Label>
            <Input
              type="date"
              value={start}
              max={end}
              onChange={(e) => setStart(e.target.value)}
              className="h-9 w-[160px] text-sm"
              data-testid="stock-movements-start"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("stockMovements.to")}</Label>
            <Input
              type="date"
              value={end}
              min={start}
              onChange={(e) => setEnd(e.target.value)}
              className="h-9 w-[160px] text-sm"
              data-testid="stock-movements-end"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">{t("common.product")}</Label>
            <Select value={product} onValueChange={setProduct}>
              <SelectTrigger className="h-9 w-[200px] text-sm" data-testid="stock-movements-product">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL} data-testid="stock-movements-product-all">{t("stockMovements.allProducts")}</SelectItem>
                {products.map((p) => (
                  <SelectItem key={p} value={p} data-testid={`stock-movements-product-${p}`}>{td(p)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Card data-testid="sm-card-in">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ArrowDownToLine className="h-4 w-4 text-[hsl(var(--success))]" /> {t("stockMovements.totalIn")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums text-[hsl(var(--success))]" data-testid="sm-total-in">
              {fmt(totals.in_kg, 2)} <span className="text-sm font-normal text-muted-foreground">kg</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="sm-card-out">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ArrowUpFromLine className="h-4 w-4 text-[hsl(var(--danger))]" /> {t("stockMovements.totalOut")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums text-[hsl(var(--danger))]" data-testid="sm-total-out">
              {fmt(totals.out_kg, 2)} <span className="text-sm font-normal text-muted-foreground">kg</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="sm-card-net">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <Scale className="h-4 w-4" /> {t("stockMovements.netChange")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums" data-testid="sm-total-net">
              {totals.net_kg > 0 ? "+" : ""}{fmt(totals.net_kg, 2)} <span className="text-sm font-normal text-muted-foreground">kg</span>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="sm-card-count">
          <CardContent className="p-5">
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <ListChecks className="h-4 w-4" /> {t("stockMovements.movements")}
            </div>
            <div className="mt-2 font-mono text-2xl font-semibold tabular-nums" data-testid="sm-total-count">
              {totals.movement_count}
            </div>
            <div className="mt-1 text-xs text-muted-foreground">
              {t("stockMovements.acrossProducts", { n: totals.product_count })}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Losses by reason */}
      <Card className="mt-4" data-testid="losses-by-reason-card">
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <CardTitle className="flex items-center gap-2 text-base">
                <Trash2 className="h-4 w-4 text-[hsl(var(--danger))]" /> {t("stockMovements.losses.title")}
              </CardTitle>
              <p className="mt-1 text-xs text-muted-foreground">{t("stockMovements.losses.subtitle")}</p>
            </div>
            {losses.total_count > 0 && (
              <div className="text-right">
                <div className="font-mono text-xl font-semibold tabular-nums text-[hsl(var(--danger))]" data-testid="losses-total-kg">
                  -{fmt(losses.total_kg, 2)} <span className="text-sm font-normal text-muted-foreground">kg</span>
                </div>
                {losses.total_value > 0 && (
                  <div className="font-mono text-sm font-semibold tabular-nums text-[hsl(var(--danger))]" data-testid="losses-total-value">
                    ~{fmt(losses.total_value, 2)} {losses.currency || "RON"}
                  </div>
                )}
                <div className="text-xs text-muted-foreground" data-testid="losses-total-count">
                  {t("stockMovements.losses.writeOffs", { n: losses.total_count })}
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-10 w-full" />)}
            </div>
          ) : losses.by_reason.length === 0 ? (
            <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground" data-testid="losses-empty">
              <CheckCircle2 className="h-5 w-5 text-[hsl(var(--success))]" />
              {t("stockMovements.losses.empty")}
            </div>
          ) : (
            <div className="space-y-4">
              {losses.by_reason.map((b) => {
                const color = REASON_COLOR[b.reason] || CUSTOM_REASON_COLOR;
                return (
                  <div key={b.reason} data-testid={`loss-reason-row-${b.reason}`}>
                    <div className="flex items-center justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} aria-hidden="true" />
                        <span className="text-sm font-medium">{reasonLabel(b.reason)}</span>
                        <Badge variant="outline" className="text-[10px]" data-testid={`loss-reason-count-${b.reason}`}>
                          {t("stockMovements.losses.writeOffs", { n: b.count })}
                        </Badge>
                      </div>
                      <div className="whitespace-nowrap text-right font-mono text-sm font-semibold tabular-nums" data-testid={`loss-reason-kg-${b.reason}`}>
                        {fmt(b.kg, 2)} kg
                        {b.value > 0 && (
                          <span className="ml-2 text-[hsl(var(--danger))]" data-testid={`loss-reason-value-${b.reason}`}>
                            ~{fmt(b.value, 2)} {losses.currency || "RON"}
                          </span>
                        )}
                        <span className="ml-2 text-xs font-normal text-muted-foreground">{fmt(b.share, 1)}%</span>
                      </div>
                    </div>
                    <div className="mt-1.5 h-2 w-full overflow-hidden rounded-full bg-muted">
                      <div
                        className="h-full rounded-full"
                        style={{ width: `${Math.max(b.share, 2)}%`, backgroundColor: color, opacity: 0.85 }}
                      />
                    </div>
                    <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                      {b.products.slice(0, 4).map((p) => (
                        <span key={p.product} data-testid={`loss-reason-product-${b.reason}-${p.product}`}>
                          {td(p.product)} · {fmt(p.kg, 2)} kg{p.value > 0 ? ` · ~${fmt(p.value, 2)} ${losses.currency || "RON"}` : ""}
                        </span>
                      ))}
                      {b.products.length > 4 && <span>+{b.products.length - 4}</span>}
                    </div>
                  </div>
                );
              })}
              <p className="text-[11px] text-muted-foreground" data-testid="losses-value-note">
                {t("stockMovements.losses.estValueNote")}
                {losses.uncovered_kg > 0 && (
                  <span className="ml-1 text-[hsl(var(--warning))]" data-testid="losses-uncovered-note">
                    {t("stockMovements.losses.uncovered", { kg: fmt(losses.uncovered_kg, 2) })}
                  </span>
                )}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-product balance */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">{t("stockMovements.balanceTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto thin-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("common.product")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.opening")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.inKg")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.outKg")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.closing")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 4 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 5 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : !hasRows ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground" data-testid="sm-balance-empty">
                      <PackageSearch className="mx-auto mb-2 h-8 w-8 text-muted-foreground/40" />
                      {t("stockMovements.empty")}
                    </TableCell>
                  </TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.product} data-testid={`sm-balance-row-${r.product}`}>
                    <TableCell className="font-medium">{td(r.product)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-muted-foreground">{fmt(r.opening_kg, 2)}</TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-[hsl(var(--success))]">
                      {r.in_kg > 0 ? `+${fmt(r.in_kg, 2)}` : "0"}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums text-[hsl(var(--danger))]">
                      {r.out_kg > 0 ? `-${fmt(r.out_kg, 2)}` : "0"}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold tabular-nums">{fmt(r.closing_kg, 2)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
          {hasRows && (
            <p className="mt-3 text-xs text-muted-foreground">{t("stockMovements.kgNote")}</p>
          )}
        </CardContent>
      </Card>

      {/* Detailed movements */}
      <Card className="mt-4">
        <CardHeader>
          <CardTitle className="text-base">{t("stockMovements.detailTitle")}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto thin-scroll">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("stockMovements.date")}</TableHead>
                  <TableHead>{t("common.product")}</TableHead>
                  <TableHead>{t("stockMovements.direction")}</TableHead>
                  <TableHead>{t("stockMovements.movementType")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.qty")}</TableHead>
                  <TableHead className="text-right">{t("stockMovements.kg")}</TableHead>
                  <TableHead>{t("stockMovements.lot")}</TableHead>
                  <TableHead>{t("stockMovements.reference")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <TableRow key={i}>
                      {Array.from({ length: 8 }).map((__, j) => (
                        <TableCell key={j}><Skeleton className="h-4 w-full" /></TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : !hasMoves ? (
                  <TableRow>
                    <TableCell colSpan={8} className="py-10 text-center text-sm text-muted-foreground" data-testid="sm-detail-empty">
                      {t("stockMovements.emptyDetail")}
                    </TableCell>
                  </TableRow>
                ) : movements.map((m, i) => (
                  <TableRow key={`${m.date}-${m.product}-${i}`} data-testid={`sm-detail-row-${i}`}>
                    <TableCell className="whitespace-nowrap font-mono text-xs tabular-nums">{m.date}</TableCell>
                    <TableCell className="font-medium">{td(m.product)}</TableCell>
                    <TableCell>
                      <Badge className={`${DIRECTION_STYLE[m.direction]} gap-1 text-[10px]`}>
                        {m.direction === "in"
                          ? <ArrowDownToLine className="h-3 w-3" />
                          : <ArrowUpFromLine className="h-3 w-3" />}
                        {t(`stockMovements.dir.${m.direction}`)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="text-[10px]">{typeLabel(m.type)}</Badge>
                      {m.reason && (
                        <Badge className="ml-1 border-transparent bg-[hsl(var(--danger)/0.1)] text-[10px] text-[hsl(var(--danger))] hover:bg-[hsl(var(--danger)/0.1)]"
                          data-testid={`movement-reason-${m.reason}`}>
                          {reasonLabel(m.reason)}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-mono tabular-nums">
                      {m.qty > 0 ? `${fmt(m.qty, 2)} ${m.unit}` : "—"}
                    </TableCell>
                    <TableCell className={`text-right font-mono tabular-nums ${m.direction === "in" ? "text-[hsl(var(--success))]" : "text-[hsl(var(--danger))]"}`}>
                      {m.direction === "in" ? "+" : "-"}{fmt(m.qty_kg, 2)}
                    </TableCell>
                    <TableCell className="font-mono text-xs text-muted-foreground">{m.lot_code || "—"}</TableCell>
                    <TableCell className="max-w-[240px] truncate text-xs text-muted-foreground" title={m.reference || m.note}>
                      {m.reference || m.note || "—"}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </Layout>
  );
}
